import { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import Navbar from '../../components/feature/Navbar';
import Footer from '../../components/feature/Footer';
import { products } from '../../mocks/products';
import { useCart } from '../../hooks/useCart';

export default function ProductDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { addToCart, getCartCount } = useCart();
  
  const product = products.find((p) => p.id === Number(id));
  const [selectedSize, setSelectedSize] = useState('');
  const [quantity, setQuantity] = useState(1);
  const [mainImage, setMainImage] = useState(0);
  const [showNotification, setShowNotification] = useState(false);
  const [expandedSection, setExpandedSection] = useState<string | null>('description');

  useEffect(() => {
    if (!product) return;

    document.title = `${product.name} | PEL Partner Company`;
    const metaDesc = document.querySelector('meta[name="description"]');
    if (metaDesc) metaDesc.setAttribute('content', `${product.name} — ${product.description ? product.description.slice(0, 130) : 'Premium radiator from PEL Partner Company. Available in multiple sizes. Fast delivery across Belgium, UK & Europe.'}` );

    const schemaId = 'schema-product-detail';
    const existing = document.getElementById(schemaId);
    if (existing) existing.remove();

    const script = document.createElement('script');
    script.id = schemaId;
    script.type = 'application/ld+json';
    script.text = JSON.stringify({
      '@context': 'https://schema.org',
      '@type': 'Product',
      name: product.name,
      image: product.image,
      description: product.description || `${product.name} — premium radiator from PEL Partner Company.`,
      sku: String(product.id),
      brand: {
        '@type': 'Brand',
        name: 'PEL Partner Company',
      },
      offers: {
        '@type': 'Offer',
        url: `${import.meta.env.VITE_SITE_URL || 'https://example.com'}/product/${product.id}`,
        priceCurrency: 'EUR',
        price: product.price.toFixed(2),
        availability: product.inStock ? 'https://schema.org/InStock' : 'https://schema.org/OutOfStock',
        seller: {
          '@type': 'Organization',
          name: 'PEL Partner Company',
        },
      },
      aggregateRating: {
        '@type': 'AggregateRating',
        ratingValue: product.rating,
        reviewCount: product.reviews,
      },
      breadcrumb: {
        '@type': 'BreadcrumbList',
        itemListElement: [
          { '@type': 'ListItem', position: 1, name: 'Home', item: import.meta.env.VITE_SITE_URL || 'https://example.com' },
          { '@type': 'ListItem', position: 2, name: 'Products', item: `${import.meta.env.VITE_SITE_URL || 'https://example.com'}/products` },
          { '@type': 'ListItem', position: 3, name: product.name, item: `${import.meta.env.VITE_SITE_URL || 'https://example.com'}/product/${product.id}` },
        ],
      },
    });
    document.head.appendChild(script);

    return () => {
      const el = document.getElementById(schemaId);
      if (el) el.remove();
    };
  }, [product]);

  if (!product) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-bold mb-4">Product not found</h2>
          <Link to="/products" className="text-black underline cursor-pointer">
            Back to Products
          </Link>
        </div>
      </div>
    );
  }

  const relatedProducts = products
    .filter((p) => p.category === product.category && p.id !== product.id)
    .slice(0, 4);

  const thumbnails = [product.image, product.image, product.image, product.image];

  const handleAddToCart = () => {
    if (!selectedSize) {
      alert('Please select a size');
      return;
    }

    addToCart({
      id: product.id,
      name: product.name,
      price: product.price,
      image: product.image,
      size: selectedSize,
      quantity,
    });

    setShowNotification(true);
    setTimeout(() => setShowNotification(false), 3000);
  };

  const toggleSection = (section: string) => {
    setExpandedSection(expandedSection === section ? null : section);
  };

  return (
    <div className="min-h-screen bg-white">
      <Navbar cartItemCount={getCartCount()} />

      {/* Notification */}
      {showNotification && (
        <div className="fixed top-24 right-6 bg-black text-white px-6 py-4 rounded-lg shadow-lg z-50 animate-fade-in">
          <div className="flex items-center gap-3">
            <i className="ri-check-line text-2xl"></i>
            <span className="font-medium">Added to cart successfully!</span>
          </div>
        </div>
      )}

      {/* Breadcrumb */}
      <div className="bg-white pt-24 pb-6">
        <div className="max-w-[1400px] mx-auto px-6">
          <div className="flex items-center gap-2 text-sm text-gray-600">
            <Link to="/" className="hover:text-black cursor-pointer">Home</Link>
            <i className="ri-arrow-right-s-line"></i>
            <Link to="/products" className="hover:text-black cursor-pointer">Products</Link>
            <i className="ri-arrow-right-s-line"></i>
            <span className="text-black">{product.category}</span>
            <i className="ri-arrow-right-s-line"></i>
            <span className="text-black">{product.name}</span>
          </div>
        </div>
      </div>

      {/* Product Display */}
      <section className="py-8">
        <div className="max-w-[1400px] mx-auto px-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            {/* Left Column - Images */}
            <div>
              <div className="relative rounded-3xl overflow-hidden mb-4 aspect-square">
                <img
                  src={thumbnails[mainImage]}
                  alt={product.name}
                  className="w-full h-full object-cover"
                />
                <div className="absolute top-6 left-6 flex gap-3">
                  <button className="backdrop-blur-md bg-white/30 text-white px-4 py-2 rounded-full text-sm font-medium hover:bg-white/40 transition-colors whitespace-nowrap">
                    <i className="ri-360-line mr-2"></i>360° View
                  </button>
                  <button className="backdrop-blur-md bg-white/30 text-white px-4 py-2 rounded-full text-sm font-medium hover:bg-white/40 transition-colors whitespace-nowrap">
                    <i className="ri-zoom-in-line mr-2"></i>Zoom
                  </button>
                  <button className="backdrop-blur-md bg-white/30 text-white px-4 py-2 rounded-full text-sm font-medium hover:bg-white/40 transition-colors whitespace-nowrap">
                    <i className="ri-share-line mr-2"></i>Share
                  </button>
                </div>
              </div>

              {/* Thumbnails */}
              <div className="grid grid-cols-4 gap-3">
                {thumbnails.map((thumb, index) => (
                  <button
                    key={index}
                    onClick={() => setMainImage(index)}
                    className={`rounded-xl overflow-hidden aspect-square cursor-pointer transition-all ${
                      mainImage === index ? 'ring-2 ring-black' : 'opacity-60 hover:opacity-100'
                    }`}
                  >
                    <img src={thumb} alt={`View ${index + 1}`} className="w-full h-full object-cover" />
                  </button>
                ))}
              </div>
            </div>

            {/* Right Column - Details */}
            <div>
              <h1 className="font-sans text-5xl font-bold text-black mb-4">{product.name}</h1>
              
              <div className="flex items-center gap-3 mb-6">
                <div className="flex items-center gap-1">
                  {[...Array(5)].map((_, i) => (
                    <i
                      key={i}
                      className={`ri-star-${i < Math.floor(product.rating) ? 'fill' : 'line'} text-yellow-500 text-lg`}
                    ></i>
                  ))}
                </div>
                <span className="text-gray-600">({product.reviews} reviews)</span>
              </div>

              <div className="flex items-center gap-3 mb-6">
                {product.originalPrice && (
                  <span className="text-2xl text-gray-400 line-through">
                    ${product.originalPrice.toFixed(2)}
                  </span>
                )}
                <span className="text-4xl font-bold text-black">
                  ${product.price.toFixed(2)}
                </span>
              </div>

              <div className="flex items-center gap-3 mb-8">
                <span className="px-4 py-2 border border-gray-300 rounded-full text-sm">
                  {product.category}
                </span>
                <span className="text-gray-600 text-sm">{product.material}</span>
                {product.inStock && (
                  <span className="text-green-600 text-sm flex items-center gap-1">
                    <i className="ri-check-line"></i>In Stock
                  </span>
                )}
              </div>

              {/* Size Selector */}
              <div className="mb-6">
                <label className="block text-sm font-medium text-black mb-3">Select Size</label>
                <div className="flex flex-wrap gap-3">
                  {product.sizes.map((size) => (
                    <button
                      key={size}
                      onClick={() => setSelectedSize(size)}
                      className={`px-6 py-3 rounded-full text-sm font-medium transition-all whitespace-nowrap ${
                        selectedSize === size
                          ? 'bg-black text-white'
                          : 'bg-white text-gray-700 border border-gray-300 hover:border-black'
                      }`}
                    >
                      {size}
                    </button>
                  ))}
                </div>
              </div>

              {/* Quantity Selector */}
              <div className="mb-8">
                <label className="block text-sm font-medium text-black mb-3">Quantity</label>
                <div className="flex items-center gap-4">
                  <button
                    onClick={() => setQuantity(Math.max(1, quantity - 1))}
                    className="w-12 h-12 rounded-full border border-gray-300 flex items-center justify-center hover:border-black transition-colors cursor-pointer"
                  >
                    <i className="ri-subtract-line text-xl"></i>
                  </button>
                  <span className="text-2xl font-semibold w-12 text-center">{quantity}</span>
                  <button
                    onClick={() => setQuantity(quantity + 1)}
                    className="w-12 h-12 rounded-full border border-gray-300 flex items-center justify-center hover:border-black transition-colors cursor-pointer"
                  >
                    <i className="ri-add-line text-xl"></i>
                  </button>
                </div>
              </div>

              {/* Action Buttons */}
              <div className="space-y-3 mb-8">
                <button
                  onClick={handleAddToCart}
                  className="w-full bg-black text-white py-4 rounded-full text-sm font-medium hover:bg-gray-800 transition-colors whitespace-nowrap"
                >
                  Add to Cart
                </button>
                <button className="w-full bg-white text-black py-4 rounded-full text-sm font-medium border border-gray-300 hover:border-black transition-colors whitespace-nowrap">
                  <i className="ri-heart-line mr-2"></i>Add to Wishlist
                </button>
              </div>

              {/* Accordion Sections */}
              <div className="space-y-3">
                <div className="border border-gray-200 rounded-xl overflow-hidden">
                  <button
                    onClick={() => toggleSection('description')}
                    className="w-full px-6 py-4 flex items-center justify-between bg-white hover:bg-gray-50 transition-colors cursor-pointer"
                  >
                    <span className="font-medium text-black">Description</span>
                    <i className={`ri-arrow-${expandedSection === 'description' ? 'up' : 'down'}-s-line text-xl`}></i>
                  </button>
                  {expandedSection === 'description' && (
                    <div className="px-6 py-4 bg-gray-50 text-gray-700 text-sm leading-relaxed">
                      {product.description}
                    </div>
                  )}
                </div>

                <div className="border border-gray-200 rounded-xl overflow-hidden">
                  <button
                    onClick={() => toggleSection('shipping')}
                    className="w-full px-6 py-4 flex items-center justify-between bg-white hover:bg-gray-50 transition-colors cursor-pointer"
                  >
                    <span className="font-medium text-black">Shipping Info</span>
                    <i className={`ri-arrow-${expandedSection === 'shipping' ? 'up' : 'down'}-s-line text-xl`}></i>
                  </button>
                  {expandedSection === 'shipping' && (
                    <div className="px-6 py-4 bg-gray-50 text-gray-700 text-sm leading-relaxed">
                      Free shipping on orders over $100. Standard delivery takes 3-5 business days. Express shipping available at checkout.
                    </div>
                  )}
                </div>

                <div className="border border-gray-200 rounded-xl overflow-hidden">
                  <button
                    onClick={() => toggleSection('returns')}
                    className="w-full px-6 py-4 flex items-center justify-between bg-white hover:bg-gray-50 transition-colors cursor-pointer"
                  >
                    <span className="font-medium text-black">Returns</span>
                    <i className={`ri-arrow-${expandedSection === 'returns' ? 'up' : 'down'}-s-line text-xl`}></i>
                  </button>
                  {expandedSection === 'returns' && (
                    <div className="px-6 py-4 bg-gray-50 text-gray-700 text-sm leading-relaxed">
                      30-day return policy. Items must be unworn and in original packaging. Return shipping is free for defective items.
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Related Products */}
      {relatedProducts.length > 0 && (
        <section className="py-16 bg-[#F5F1E8]">
          <div className="max-w-[1400px] mx-auto px-6">
            <h2 className="font-serif text-4xl font-bold text-black mb-8">You May Also Like</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
              {relatedProducts.map((relatedProduct) => (
                <Link
                  key={relatedProduct.id}
                  to={`/product/${relatedProduct.id}`}
                  className="group bg-white rounded-2xl overflow-hidden shadow-md hover:shadow-xl transition-all duration-300 hover:-translate-y-1 cursor-pointer"
                >
                  <div className="relative overflow-hidden aspect-square">
                    <img
                      src={relatedProduct.image}
                      alt={relatedProduct.name}
                      className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                    />
                  </div>
                  <div className="p-4">
                    <h3 className="text-lg font-semibold mb-2 text-black line-clamp-1">
                      {relatedProduct.name}
                    </h3>
                    <span className="text-xl font-bold text-black">
                      ${relatedProduct.price.toFixed(2)}
                    </span>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      <Footer />
    </div>
  );
}
